import React, { useState, useEffect, useRef } from 'react';
import { adminGetGallery, adminAddGallery, adminDeleteGallery } from '../../api/admin';
import AdminLayout from './AdminLayout';
import './AdminGallery.css';

const EMPTY_FORM = { title: '', category: '', media_type: 'image', sort_order: 0 };

function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    adminGetGallery().then(res => {
      if (res.success) setItems(res.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm(EMPTY_FORM); setMediaFile(null); setMediaPreview(''); setShowForm(true); setMsg('');
  };
  const closeForm = () => { setShowForm(false); setMsg(''); };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
    if (file.type.startsWith('video')) setForm(f => ({ ...f, media_type: 'video' }));
    else setForm(f => ({ ...f, media_type: 'image' }));
  };

  const handleSave = async () => {
    if (!mediaFile) { setMsg('Please select a file to upload'); return; }
    setSaving(true); setMsg('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('media', mediaFile);
      const res = await adminAddGallery(fd);
      if (res.success) { setMsg('Uploaded successfully!'); load(); setTimeout(closeForm, 1000); }
      else setMsg('Error: ' + res.message);
    } catch { setMsg('Network error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title || 'this item'}"?`)) return;
    await adminDeleteGallery(id);
    load();
  };

  const categories = ['all', ...new Set(items.map(i => i.category).filter(Boolean))];
  const filtered = filterCat === 'all' ? items : items.filter(i => i.category === filterCat);

  return (
    <AdminLayout title="Gallery">
      <div className="adm-page-header">
        <h2>Manage Gallery</h2>
        <button className="adm-btn-primary" onClick={openAdd}>Upload Media</button>
      </div>

      {showForm && (
        <div className="adm-modal-overlay" onClick={closeForm}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal__header">
              <h3>Upload to Gallery</h3>
              <button onClick={closeForm}>✕</button>
            </div>
            <div className="adm-modal__body">
              {msg && <div className={`adm-msg ${msg.startsWith('Uploaded') ? 'adm-msg--success' : 'adm-msg--error'}`}>{msg}</div>}

              <div className="adm-form-field">
                <label>Title (optional)</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. Rooftop Garden Project" />
              </div>

              <div className="adm-form-row">
                <div className="adm-form-field">
                  <label>Category</label>
                  <input value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="e.g. Rooftop, Residential" />
                </div>
                <div className="adm-form-field">
                  <label>Sort Order</label>
                  <input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: e.target.value})} />
                </div>
              </div>

              <div className="adm-form-field">
                <label>Upload Image or Video *</label>
                <div className="adm-upload-box" onClick={() => fileRef.current.click()}>
                  {mediaPreview ? (
                    form.media_type === 'video'
                      ? <video src={mediaPreview} className="adm-upload-preview" controls />
                      : <img src={mediaPreview} alt="preview" className="adm-upload-preview" />
                  ) : (
                    <div className="adm-upload-placeholder">Click to upload image or video</div>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*,video/*" style={{display:'none'}} onChange={handleFile} />
              </div>
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn-secondary" onClick={closeForm}>Cancel</button>
              <button className="adm-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category filter */}
      {categories.length > 1 && (
        <div className="adm-gallery-filters">
          {categories.map(cat => (
            <button
              key={cat}
              className={`adm-gallery-filter-btn ${filterCat === cat ? 'active' : ''}`}
              onClick={() => setFilterCat(cat)}
            >
              {cat === 'all' ? 'All' : cat}
            </button>
          ))}
        </div>
      )}

      <div className="adm-section">
        {loading ? <div className="adm-loading">Loading...</div> : (
          filtered.length === 0 ? <p className="adm-empty">No gallery items yet. Click "Upload Media" to add photos or videos.</p> : (
            <div className="adm-gallery-grid">
              {filtered.map(item => (
                <div key={item.id} className="adm-gallery-item">
                  {item.media_type === 'video' ? (
                    <video src={item.media_url} className="adm-gallery-item__media" muted />
                  ) : (
                    <img src={item.thumbnail || item.media_url} alt={item.title} className="adm-gallery-item__media" />
                  )}
                  {item.media_type === 'video' && <span className="adm-gallery-item__badge">Video</span>}
                  <div className="adm-gallery-item__overlay">
                    {item.title && <span className="adm-gallery-item__title">{item.title}</span>}
                    <button className="adm-gallery-item__delete" onClick={() => handleDelete(item.id, item.title)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminGallery;